import { useState, useEffect, useRef } from "react";
import env from "../../../../config/env";
import { Form, Modal, message } from "antd";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import { getWorkflowData } from "../../../../utils/getWorkflowData";
import { upsertSupplierPayment, upsertFinFundsTransfer } from "../../../../services";
import useSessionStore from "../../../../stores/useSessionStore";
import usePOSConfigStore from "../../../../stores/usePOSConfigStore";
import { uploadDocumentHeaders } from "../../../../utils/uploadDocumentHeaders";

/**
 * useCashManagement
 *
 * Encapsulates all cash-in / cash-out / petty-cash / bank-transfer logic
 * that was formerly inline inside PointOfsaleCore (~lines 854-1148).
 *
 * @param {Object} deps
 * @param {Object} deps.tillData      – parsed tillData from localStorage
 * @param {Object} deps.db            – database adapter (Dexie / SQLite)
 * @param {Function} deps.upsertPOSLog – POS log writer
 * @param {Function} deps.dispatchEvent – EventBus dispatcher
 */
const useCashManagement = ({ tillData, db, upsertPOSLog, dispatchEvent }) => {
  // ── state ────────────────────────────────────────────────────────────
  const [cashAddInFlag, setCashAddInFlag] = useState(false);
  const [cashManagementForm] = Form.useForm();
  const [cashIn, setCashIn] = useState(true);
  const [cashOut, setCashOut] = useState(false);
  const [pettyCashIn, setPettyCashIn] = useState(false);
  const [pettyCashOut, setPettyCashOut] = useState(false);
  const [bankTransfer, setBankTransfer] = useState(false);
  const [addCashFlag, setAddCashFlag] = useState(false);
  const [uploadedFileUrl, setUploadedFileUrl] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const cashInOutInFlightRef = useRef(false);

  const handleFileUpload = async (e) => {
    if (uploading) return;
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const buffer = await file.arrayBuffer();
      const fileData = Array.from(new Uint8Array(buffer));
      const response = await fetch(env.uploadDocumentUrl, {
        method: "POST",
        headers: uploadDocumentHeaders(),
        body: JSON.stringify({ fileName: file.name, fileType: file.type, fileData }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result?.error || "Upload failed");
      setUploadedFileUrl(result.url);
      setUploadedFileName(file.name);
      cashManagementForm.setFieldsValue({ uploadedFile: result.url });
      message.success("File uploaded successfully.");
    } catch (error) {
      console.error("File upload error:", error);
      message.error("Failed to upload file.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Clear any leftover upload state from a prior (cancelled or completed) entry
  // whenever the drawer/modal is (re)opened, so a Mandatory upload check can't be
  // satisfied by a stale file left over from a different session.
  useEffect(() => {
    if (addCashFlag) {
      setUploadedFileUrl(null);
      setUploadedFileName(null);
      cashManagementForm.setFieldsValue({ uploadedFile: undefined });
    }
  }, [addCashFlag]);

  useEffect(() => {
    const posConfig = usePOSConfigStore.getState().posConfig;
    if (addCashFlag && cashAddInFlag && posConfig?.allowPettyCashExpenses !== "Y" && posConfig?.enableBankTransferInCashOut === "Y") {
      setBankTransfer(true);
      setCashOut(false);
      setPettyCashOut(false);
      cashManagementForm.setFieldsValue({ bankTransfer: true, cashOut: false, pettyCashOut: false, type: "bankTransfer" });
    }
  }, [addCashFlag, cashAddInFlag]);

  // ── handlers ─────────────────────────────────────────────────────────

  const handleCashInOut = async (data) => {
    if (cashInOutInFlightRef.current) return;
    cashInOutInFlightRef.current = true;
    try {
    let formData = cashManagementForm.getFieldsValue(true);
    formData.key = uuidv4().replace(/-/g, "").toUpperCase();
    formData.id = uuidv4().replace(/-/g, "").toUpperCase();
    formData.date = dayjs(new Date()).format("DD-MM-YYYY HH:mm:ss");
    if (cashAddInFlag === true) {
      if (formData.type === "cashOut" || !formData.type) {
        formData.type = "cashOut";
      } else if (formData.type === "bankTransfer") {
        formData.type = "bankTransfer";
        formData.pettyCashOut = false;
      } else {
        formData.type = "pettyCashOut";
      }
    } else {
      if (formData.type === "cashIn" || !formData.type) {
        formData.type = "cashIn";
      } else {
        formData.type = "pettyCashIn";
      }
    }
    // formData.notes = data.note
    if (formData.type === "cashIn") {
      upsertPOSLog(formData, "CAI");
    } else if (formData.type === "cashOut") {
      upsertPOSLog(formData, "CAO");
    }
    setPettyCashIn(false);
    setCashOut(true);
    const pettyCashOutItems = [formData].filter((item) => item.type === "pettyCashOut" && !item?.financialAccount);

    if (pettyCashOutItems.length > 0) {
      const buildRequestString = (data) => {
        const paymentLineString = data
          .map(
            (line) => `      {
            expCategoryId: "${line.expenseCategory}"
            description: "${(line.note || "").replace(/"/g, '\\"')}"
            amount: "${line.amount}"
          }`
          )
          .join(",\n");

        return `{
        bunitId: "${tillData.tillAccess.csBunit.csBunitId}"
        date: "${dayjs(new Date()).format("YYYY-MM-DD")}"
        description: null
        supplierId: null
        paymentLine: [
    ${paymentLineString}
        ]
      }`;
      };

      const newStringRequest = buildRequestString(pettyCashOutItems);

      upsertSupplierPayment(newStringRequest);
    }

    const bankTransferData = [formData].filter((item) => item?.financialAccount);

    if (bankTransferData.length > 0) {
      const paymentMethodList = tillData?.tillAccess?.csBunit?.paymentMethodList || [];
      const buildBankTransferRequest = (data) => {
        return `{
      bunitId: "${tillData.tillAccess.csBunit.csBunitId}"
      transferDate: "${dayjs(new Date()).format("YYYY-MM-DD")}"
      toFinancialAcctId: "${data[0].financialAccount}"
      fromFinancialAcctId: "${paymentMethodList?.find((payment) => payment?.name === "Cash")?.finFinancialAccountId}"
      amount: ${parseFloat(data[0].amount)}
      description: null
      attachmentUrl: ${uploadedFileUrl ? `"${uploadedFileUrl}"` : null}
    }`;
      };

      const bankTransferRequest = buildBankTransferRequest(bankTransferData);

      upsertFinFundsTransfer(bankTransferRequest);
    }
    // DI-21: Track partial writes to detect inconsistencies
    let cashWritten = false;
    let eventWritten = false;
    try {
      await db.cashInCashOut.add(formData);
      cashWritten = true;
    } catch (err) {
      console.error("[DI-21] Cash management: cashInCashOut write failed!", {
        cashWritten, eventWritten, error: err?.message || err,
      });
    }

    cashManagementForm.resetFields();
    setAddCashFlag(false);

    let obj = { cashIn: 0, cashOut: 0, pettyCashIn: 0, pettyCashOut: 0 };
    let cashDetails = await db.cashInCashOut.toArray();

    cashDetails.map((item) => {
      if (item.type === "cashIn") {
        obj.cashIn += parseFloat(item.amount);
      } else if (item.type === "cashOut") {
        obj.cashOut += parseFloat(item.amount);
      }
      if (item.type === "pettyCashIn") {
        obj.pettyCashIn += parseFloat(item.amount);
      }
      if (item.type === "pettyCashOut") {
        obj.pettyCashOut += parseFloat(item.amount);
      }
    });
    const tillSession = useSessionStore.getState().tillSession;
    try {
      await db.tillEvents
        .where("tillSessionId")
        .equals(tillSession.tillSessionId)
        .modify((tillEvent) => {
          tillEvent.cashInOutData = obj;
        });
      eventWritten = true;
    } catch (err) {
      console.error("[DI-21] Cash management: tillEvents write failed!", {
        cashWritten, eventWritten, error: err?.message || err,
      });
    }

    if (cashWritten !== eventWritten) {
      console.error("[DI-21] Cash management partial write detected!", {
        cashWritten, eventWritten,
        formDataKey: formData.key,
        tillSessionId: tillSession?.tillSessionId,
      });
    }
    setCashIn(false);
    setCashOut(false);
    setPettyCashIn(false);
    setPettyCashOut(false);
    setBankTransfer(false);
    setAddCashFlag(false);
    setUploadedFileUrl(null);
    setUploadedFileName(null);
    } finally {
      cashInOutInFlightRef.current = false;
    }
  };

  const handleCahInOut = async (data) => {
    if (cashInOutInFlightRef.current) return;
    const { workflowRules, approvers } = await getWorkflowData();
    const cashManagementRequestRule = workflowRules.find((res) => res.eventName === "Cash Management Request");
    if (cashManagementRequestRule) {
      dispatchEvent(cashManagementRequestRule.eventName, cashManagementRequestRule.ruleName, cashManagementRequestRule.ruleCondition, approvers, data, async (eventData) => {
        if (eventData?.approvalGranted) {
          await handleCashInOut(data);
        } else {
          Modal.error({
            title: "Approval Required",
            content: "The cash management request was not approved.",
          });
        }
      });
    } else {
      await handleCashInOut(data);
    }
  };

  const onChangeCheckbox = (e) => {
    const eventId = e.target.id;
    const checkedValue = e.target.checked;
    let formData = cashManagementForm.getFieldsValue(true);
    if (cashAddInFlag) {
      if (eventId === "cashOut") {
        if (checkedValue === true) {
          setCashOut(checkedValue);
          setPettyCashOut(false);
          setBankTransfer(false);
          formData.type = "cashOut";
          cashManagementForm.setFieldsValue({ pettyCashOut: false, bankTransfer: false });
        }
      }
      if (eventId === "pettyCashOut") {
        if (checkedValue === true) {
          formData.type = "pettyCashOut";
          setPettyCashOut(checkedValue);
          setCashOut(false);
          setBankTransfer(false);
          cashManagementForm.setFieldsValue({ cashOut: false, bankTransfer: false });
        }
      }
      if (eventId === "bankTransfer") {
        if (checkedValue === true) {
          formData.type = "bankTransfer";
          setBankTransfer(checkedValue);
          setCashOut(false);
          setPettyCashOut(false);
          cashManagementForm.setFieldsValue({ cashOut: false, pettyCashOut: false });
        }
      }
    } else {
      if (eventId === "cashIn") {
        if (checkedValue === true) {
          formData.type = "cashIn";
          setCashIn(checkedValue);
          setPettyCashIn(false);
          cashManagementForm.setFieldsValue({ pettyCashIn: false });
        }
      }
      if (eventId === "pettyCashIn") {
        if (checkedValue === true) {
          formData.type = "pettyCashIn";
          setCashIn(false);
          setPettyCashIn(checkedValue);
          cashManagementForm.setFieldsValue({ cashIn: false });
        }
      }
    }
    cashManagementForm.setFieldsValue(formData);
  };

  // ── public API ───────────────────────────────────────────────────────
  return {
    // state
    cashAddInFlag,
    setCashAddInFlag,
    cashManagementForm,
    cashIn,
    setCashIn,
    cashOut,
    setCashOut,
    pettyCashIn,
    setPettyCashIn,
    pettyCashOut,
    setPettyCashOut,
    bankTransfer,
    setBankTransfer,
    addCashFlag,
    setAddCashFlag,

    // file upload (bank transfer)
    fileInputRef,
    handleFileUpload,
    uploadedFileUrl,
    uploadedFileName,
    setUploadedFileUrl,
    setUploadedFileName,
    uploading,

    // handlers
    handleCashInOut,
    handleCahInOut,
    onChangeCheckbox,
  };
};

export default useCashManagement;
